import moment, { type Moment } from 'moment';

/**
 * Dashboard helpers aligned with Cyrian-Backend `campaign-calendar.util.ts`:
 * - `parseCampaignAnchorYear` / bracket regex / `midAnchor*` match backend.
 * - Main wrap (`mainStart` after `mainEnd` on the month-day circle) matches backend.
 *
 * Known deltas (acceptable for normal data; defaults are Apr–Sep mid, no mid-wrap):
 * - Backend uses UTC (`getUTC*`) for import/overlap; this file uses local `moment` for RangePicker UX.
 * - Backend `isDateInMidWindow` does not span two calendar years if mid season “wraps”; we still build
 *   a two-year mid range when `midWindowWraps` — only diverges if operators configure a wrapping mid crop.
 * - If `year` is invalid and the name has no `[YYYY…]` bracket, we fall back to `moment().year()` for
 *   range construction; backend then uses year-agnostic month-day rules — rare mis-sync for broken rows.
 */
/** Mirrors Cyrian-Backend `campaign-calendar.util` anchor for main/mid windows. */
function parseCampaignAnchorYear(c: cyrian.api.campaigns.Campaign): number | null {
  const y = parseInt(String(c.year), 10);
  return Number.isFinite(y) ? y : null;
}

/** Second year in `[YYYY - YYYY] …` names; null for `[YYYY] …`. */
function parseBracketEndYearFromName(c: cyrian.api.campaigns.Campaign): number | null {
  const m = String(c.name ?? '')
    .trim()
    .match(/^\[(\d{4})(?:\s*-\s*(\d{4}))?\]/);
  if (!m?.[2]) return null;
  const y2 = parseInt(m[2], 10);
  return Number.isFinite(y2) ? y2 : null;
}

/** First year in `[YYYY …` from name (fallback when API `year` is missing for range construction). */
function parseBracketStartYearFromName(c: cyrian.api.campaigns.Campaign): number | null {
  const m = String(c.name ?? '')
    .trim()
    .match(/^\[(\d{4})(?:\s*-\s*(\d{4}))?\]/);
  if (!m?.[1]) return null;
  const y1 = parseInt(m[1], 10);
  return Number.isFinite(y1) ? y1 : null;
}

/**
 * Calendar year that contains this campaign's mid-crop segment (same logic as backend `midAnchorUtcYear`).
 * Dashboard uses local `moment` dates; anchor year must match backend for season buttons / default campaign pick.
 */
function midAnchorYear(c: cyrian.api.campaigns.Campaign): number | null {
  const Y = parseCampaignAnchorYear(c);
  if (Y == null) return null;
  const endY = parseBracketEndYearFromName(c);
  if (endY != null) return endY;
  return Y;
}

/** Main window wraps across calendar years when start (month/day) is after end (month/day) on the circle. */
function mainWindowWraps(c: cyrian.api.campaigns.Campaign): boolean {
  if (c.mainStartMonth !== c.mainEndMonth) {
    return c.mainStartMonth > c.mainEndMonth;
  }
  return c.mainStartDay > c.mainEndDay;
}

function midWindowWraps(c: cyrian.api.campaigns.Campaign): boolean {
  if (c.midStartMonth !== c.midEndMonth) {
    return c.midStartMonth > c.midEndMonth;
  }
  return c.midStartDay > c.midEndDay;
}

export function mainMidRangesFromCampaign(c: cyrian.api.campaigns.Campaign): {
  main: [Moment, Moment];
  mid: [Moment, Moment];
} {
  const Y = parseCampaignAnchorYear(c) ?? parseBracketStartYearFromName(c) ?? moment().year();
  const wrapMain = mainWindowWraps(c);
  const mainEndYear = wrapMain ? Y + 1 : Y;
  const mainStart = moment({ year: Y, month: c.mainStartMonth - 1, day: c.mainStartDay }).startOf('day');
  const mainEnd = moment({
    year: mainEndYear,
    month: c.mainEndMonth - 1,
    day: c.mainEndDay,
  }).startOf('day');

  const midBaseYear = midAnchorYear(c) ?? (wrapMain ? Y + 1 : Y);
  const midStart = moment({
    year: midBaseYear,
    month: c.midStartMonth - 1,
    day: c.midStartDay,
  }).startOf('day');
  let midEndYear = midBaseYear;
  if (midWindowWraps(c)) {
    midEndYear = midBaseYear + 1;
  }
  const midEnd = moment({
    year: midEndYear,
    month: c.midEndMonth - 1,
    day: c.midEndDay,
  }).startOf('day');

  return { main: [mainStart, mainEnd], mid: [midStart, midEnd] };
}

function isInRangeInclusive(d: Moment, start: Moment, end: Moment): boolean {
  return !d.isBefore(start, 'day') && !d.isAfter(end, 'day');
}

export function pickCampaignIdForDate(campaigns: cyrian.api.campaigns.Campaign[], d: Moment): number | undefined {
  for (const c of campaigns) {
    const { main, mid } = mainMidRangesFromCampaign(c);
    if (isInRangeInclusive(d, main[0], main[1]) || isInRangeInclusive(d, mid[0], mid[1])) {
      return c.id;
    }
  }
  return undefined;
}
